import { supabase } from '../supabaseClient';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface LatLng {
  latitude: number;
  longitude: number;
}

// Parse EWKB hex string to extract lat/lng
function parseEwkbPoint(hex: string): LatLng | null {
  if (!hex || hex.length < 42) return null;
  try {
    // EWKB format: byte order (2) + type (8) + SRID (8) + X (16) + Y (16) = 50 chars minimum
    const byteOrder = hex.substring(0, 2);
    const isLittleEndian = byteOrder === '01';

    function readDouble(offset: number): number {
      const bytes: number[] = [];
      for (let i = 0; i < 8; i++) {
        const byteHex = hex.substring(offset + i * 2, offset + i * 2 + 2);
        bytes.push(parseInt(byteHex, 16));
      }
      if (!isLittleEndian) {
        bytes.reverse();
      }
      const buffer = new ArrayBuffer(8);
      const view = new DataView(buffer);
      bytes.forEach((b, i) => view.setUint8(i, b));
      return view.getFloat64(0, true); // true = little-endian
    }

    // Check if SRID flag is set (type includes 0x20000000)
    let offset = 2; // after byte order
    const typeHex = hex.substring(offset, offset + 8);
    offset += 8;

    // Parse type to check for SRID
    const typeBytes: number[] = [];
    for (let i = 0; i < 4; i++) {
      typeBytes.push(parseInt(typeHex.substring(i * 2, i * 2 + 2), 16));
    }
    const typeBuffer = new ArrayBuffer(4);
    const typeView = new DataView(typeBuffer);
    typeBytes.forEach((b, i) => typeView.setUint8(i, b));
    const typeValue = typeView.getUint32(0, isLittleEndian);

    const hasSRID = (typeValue & 0x20000000) !== 0;
    if (hasSRID) {
      offset += 8; // skip SRID (4 bytes = 8 hex chars)
    }

    const lng = readDouble(offset);
    offset += 16;
    const lat = readDouble(offset);

    if (isNaN(lat) || isNaN(lng)) return null;
    return { latitude: lat, longitude: lng };
  } catch (e) {
    console.error('Error parsing EWKB:', (e as Error).message);
    return null;
  }
}

/**
 * A guard whose device has not reported for longer than this is shown as having
 * lost signal rather than as a live position. Without it, a handset that stopped
 * reporting an hour ago is indistinguishable on the map from one standing still.
 */
export const LIVE_LOCATION_STALE_AFTER_MS = 5 * 60 * 1000;

export interface GuardLocation {
  id: string;
  guardId: string;
  fullName: string;
  avatarUrl: string | null;
  latitude: number;
  longitude: number;
  heading: number | null;
  speed: number | null;
  accuracy: number | null;
  battery: number | null;
  updatedAt: string;
  isOnShift: boolean;
  /** On shift, but the last position is older than LIVE_LOCATION_STALE_AFTER_MS. */
  isLocationStale: boolean;
  /** Age of the last position in ms, or null when it cannot be determined. */
  lastPingAgeMs: number | null;
  siteId: string | null;
  siteName: string;
}

/**
 * An active shift whose device has stopped reporting. Distinct from a stale
 * GuardLocation: a guard whose first ping never landed has no guard_live_locations
 * row at all, so they are invisible to fetchGuardLocations entirely.
 */
export interface StaleTrackingExecution {
  shiftExecutionId: string;
  guardId: string;
  guardName: string;
  siteId: string | null;
  siteName: string;
  actualClockInAt: string;
  lastLocationAt: string | null;
  silentForSeconds: number;
}

type MaybeSingle<T> = T | T[] | null | undefined;

function unwrapRelation<T>(value: MaybeSingle<T>): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

export const guardLocationService = {
  async fetchGuardLocations(): Promise<GuardLocation[]> {
    const { data, error } = await supabase
      .from('guard_live_locations')
      .select('*, profiles(full_name, avatar_url)');

    if (error) throw error;

    const records = (data ?? []) as Array<{
      guard_id: string;
      location: string | null;
      heading: number | null;
      speed: number | null;
      accuracy: number | null;
      battery: number | null;
      updated_at: string;
      shift_execution_id?: string | null;
      profiles?: MaybeSingle<{ full_name: string | null; avatar_url: string | null }>;
    }>;

    const guardIds = [...new Set(records.map((record) => record.guard_id).filter(Boolean))];
    const activeShiftGuardIds = new Set<string>();
    const latestExecutionByGuard = new Map<string, {
      guard_id: string;
      site_id: string | null;
      actual_clock_in_at: string | null;
      actual_clock_out_at: string | null;
      status: string | null;
      sites?: MaybeSingle<{ name: string | null }>;
    }>();

    if (guardIds.length) {
      // Pull the newest execution per guard so the live view follows the duty-based model.
      const { data: executions, error: executionsError } = await supabase
        .from('shift_executions')
        .select(`
          guard_id,
          site_id,
          actual_clock_in_at,
          actual_clock_out_at,
          status,
          sites:sites!shift_executions_site_id_fkey(name)
        `)
        .in('guard_id', guardIds)
        .not('actual_clock_in_at', 'is', null)
        .order('actual_clock_in_at', { ascending: false });

      if (executionsError) {
        throw executionsError;
      }

      type ExecutionRow = {
        guard_id: string;
        site_id: string | null;
        actual_clock_in_at: string | null;
        actual_clock_out_at: string | null;
        status: string | null;
        sites?: MaybeSingle<{ name: string | null }>;
      };

      ((executions ?? []) as unknown as ExecutionRow[]).forEach((execution) => {
        if (!latestExecutionByGuard.has(execution.guard_id)) {
          latestExecutionByGuard.set(execution.guard_id, execution);
        }
        if (execution.status === 'active' && execution.actual_clock_in_at && !execution.actual_clock_out_at) {
          activeShiftGuardIds.add(execution.guard_id);
          const current = latestExecutionByGuard.get(execution.guard_id);
          if (!current || current.actual_clock_out_at) {
            latestExecutionByGuard.set(execution.guard_id, execution);
          }
        }
      });
    }

    const now = Date.now();

    return records.map((record) => {
      const coords = record.location ? parseEwkbPoint(record.location) : null;
      const latestExecution = latestExecutionByGuard.get(record.guard_id);
      const profile = unwrapRelation(record.profiles);
      const site = unwrapRelation(latestExecution?.sites);
      const updatedAtMs = Date.parse(record.updated_at);
      const lastPingAgeMs = Number.isNaN(updatedAtMs) ? null : now - updatedAtMs;
      // An unreadable timestamp counts as stale: presenting a position of unknown
      // age as live is exactly the failure this guard rail exists to prevent.
      const isLocationStale = lastPingAgeMs === null || lastPingAgeMs > LIVE_LOCATION_STALE_AFTER_MS;
      return {
        // guard_live_locations no longer carries a synthetic row id in the live schema.
        id: record.guard_id,
        guardId: record.guard_id,
        fullName: profile?.full_name ?? 'Unknown',
        avatarUrl: profile?.avatar_url ?? null,
        latitude: coords?.latitude ?? 0,
        longitude: coords?.longitude ?? 0,
        heading: record.heading,
        speed: record.speed,
        accuracy: record.accuracy,
        battery: record.battery,
        updatedAt: record.updated_at,
        isOnShift: activeShiftGuardIds.has(record.guard_id),
        isLocationStale,
        lastPingAgeMs,
        siteId: latestExecution?.site_id ?? null,
        siteName: site?.name ?? 'Unassigned',
      };
    });
  },

  /**
   * Active executions with no location fix for the server's tracking-gap
   * threshold. Requires an admin/manager-class role; other callers get an empty
   * list rather than an error so the live view still renders for them.
   */
  async fetchStaleTrackingExecutions(): Promise<StaleTrackingExecution[]> {
    const { data, error } = await supabase.rpc('get_stale_tracking_executions');

    if (error) {
      console.warn('Stale tracking check unavailable:', error.message);
      return [];
    }

    type StaleRow = {
      shift_execution_id: string;
      guard_id: string;
      guard_name: string | null;
      site_id: string | null;
      site_name: string | null;
      actual_clock_in_at: string;
      last_location_at: string | null;
      silent_for_seconds: number | null;
    };

    return ((data ?? []) as StaleRow[]).map((row) => ({
      shiftExecutionId: row.shift_execution_id,
      guardId: row.guard_id,
      guardName: row.guard_name ?? 'Unknown',
      siteId: row.site_id,
      siteName: row.site_name ?? 'Unassigned',
      actualClockInAt: row.actual_clock_in_at,
      lastLocationAt: row.last_location_at,
      silentForSeconds: row.silent_for_seconds ?? 0,
    }));
  },

  subscribeToLocationChanges(
    callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void,
  ): () => void {
    const channel = supabase
      .channel('guard_live_locations_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'guard_live_locations',
        },
        (payload) => {
          callback(payload);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};
